import { TEST_IDS } from '@argus-labs/auth-shared/test-ids'
import { VerifyType } from '@argus-labs/auth-shared/verify'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Countdown } from '@/components/ui/countdown'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'
import { PageCard } from '@/components/ui/page-card'
import { ShimmerText } from '@/components/ui/shimmer-text'
import { Text, text } from '@/components/ui/text'
import { authClient } from '@/lib/auth-client'
import { authFlow, flowDestination, flowHeaders, upgradeFailureReason } from '@/lib/auth-flow'
import { otpVerification } from '@/lib/otp-verification'
import { invalidateSessionQuery } from '@/lib/session'
import { toast } from '@/lib/toast'
import {
  BETTER_AUTH_EMAIL_OTP_ERROR_CODES,
  type BetterAuthAPIError,
  commonSearchSchema,
  generateTitle,
} from '@/lib/utils'

const verifyPageSchema = commonSearchSchema.extend({
  email: z.email(),
  type: z.enum([VerifyType.ConfirmEmail, VerifyType.PasswordReset, VerifyType.ChangeEmail]),
})

type VerifyPageType = VerifyType.ConfirmEmail | VerifyType.PasswordReset | VerifyType.ChangeEmail

export const Route = createFileRoute('/verify')({
  validateSearch: verifyPageSchema,
  component: Verify,
  head: () => ({ meta: [{ title: generateTitle('Enter 6-digit Code') }] }),
  beforeLoad: async ({ search }) => {
    if (!search.email || !search.type) {
      throw redirect({ to: '/login' })
    }
  },
})

const TITLES = {
  [VerifyType.ConfirmEmail]: 'Verify Account',
  [VerifyType.PasswordReset]: 'Forgot Password',
  [VerifyType.ChangeEmail]: 'Verify New Email',
}

type OtpErrorCode =
  | typeof BETTER_AUTH_EMAIL_OTP_ERROR_CODES.INVALID_OTP
  | typeof BETTER_AUTH_EMAIL_OTP_ERROR_CODES.OTP_EXPIRED

const OTP_ERROR_MESSAGES: Record<OtpErrorCode, string> = {
  [BETTER_AUTH_EMAIL_OTP_ERROR_CODES.INVALID_OTP]:
    'Invalid 6-digit code. Please check your email and try again.',
  [BETTER_AUTH_EMAIL_OTP_ERROR_CODES.OTP_EXPIRED]: '6-digit code already expired.',
}

type OtpSubmitResult =
  | { kind: 'success' }
  | { kind: 'otp-error'; code: OtpErrorCode }
  | { kind: 'api-error'; error: BetterAuthAPIError }

type VerifyActionResult = { data: unknown; error: null } | { data: null; error: BetterAuthAPIError }

type VerifyFlow = {
  submit: (otp: string) => Promise<OtpSubmitResult>
  resend: () => Promise<void>
}

function Verify() {
  const search = Route.useSearch()
  const { type, gameId } = search
  const isUpgrade = authFlow(search).kind === 'guestUpgrade'

  return (
    <PageCard
      title={
        isUpgrade && type === VerifyType.ConfirmEmail ? 'Verify and Save Progress' : TITLES[type]
      }
      description={
        isUpgrade && type === VerifyType.ConfirmEmail ? (
          <>
            Enter the 6-digit code from your email to link your guest progress in <b>{gameId}</b> to
            your Argus account. The code expires in 10 minutes.
          </>
        ) : (
          'Enter the 6-digit code from your email to verify. The code will expire in 10 minutes.'
        )
      }
    >
      <VerifyForm />
    </PageCard>
  )
}

function VerifyForm() {
  const search = Route.useSearch()
  const { type, email } = search
  const authContext = authFlow(search)
  const navigate = useNavigate()

  const submitVerification = async (
    action: () => Promise<VerifyActionResult>,
  ): Promise<OtpSubmitResult> => {
    // Ensure an artificial delay of 1s (shimmer text)
    const [result] = await Promise.all([
      action(),
      new Promise((resolve) => setTimeout(resolve, 1000)),
    ])

    if (result.error === null) {
      invalidateSessionQuery()
      navigate(flowDestination(authContext))
      return { kind: 'success' }
    }

    const { error } = result

    const reason = upgradeFailureReason(authContext, error.code)
    if (reason) {
      navigate({ to: '/upgrade-failed', search: { reason } })
      return { kind: 'api-error', error }
    }

    if (error.code === BETTER_AUTH_EMAIL_OTP_ERROR_CODES.INVALID_OTP) {
      return { kind: 'otp-error', code: BETTER_AUTH_EMAIL_OTP_ERROR_CODES.INVALID_OTP }
    }

    if (error.code === BETTER_AUTH_EMAIL_OTP_ERROR_CODES.OTP_EXPIRED) {
      return { kind: 'otp-error', code: BETTER_AUTH_EMAIL_OTP_ERROR_CODES.OTP_EXPIRED }
    }

    return { kind: 'api-error', error }
  }

  const runResend = async (action: () => Promise<{ error?: BetterAuthAPIError | null }>) => {
    try {
      const { error } = await action()

      if (error) {
        toast.fromError(error)
      }
    } catch {
      toast.fromPreset('NETWORK_ERROR')
    }
  }

  const flows = {
    [VerifyType.ConfirmEmail]: {
      submit: (otp) =>
        submitVerification(() =>
          authClient.emailOtp.verifyEmail({
            email,
            otp,
            fetchOptions: {
              headers: flowHeaders(authContext),
            },
          }),
        ),
      resend: () => runResend(() => otpVerification.verifyEmail(email)),
    },
    [VerifyType.PasswordReset]: {
      submit: async (otp) => {
        navigate({
          to: '/forgot-password',
          search: { email, otp },
        })
        return { kind: 'success' }
      },
      resend: () => runResend(() => otpVerification.requestPasswordReset(email)),
    },
    [VerifyType.ChangeEmail]: {
      submit: (otp) =>
        submitVerification(() =>
          authClient.emailOtp.changeEmail({
            newEmail: email,
            otp,
          }),
        ),
      resend: () => runResend(() => otpVerification.requestEmailChange(email)),
    },
  } satisfies Record<VerifyPageType, VerifyFlow>

  const flow = flows[type]

  return (
    <OtpForm onSubmit={flow.submit}>
      <ResendCode onClick={flow.resend} />
    </OtpForm>
  )
}

function OtpForm({
  onSubmit,
  children,
}: {
  onSubmit: (otp: string) => Promise<OtpSubmitResult>
  children: React.ReactNode
}) {
  const form = useForm<{ otp: string }>({
    // We don't need zod resolver here because we're not validating the form client-side
    defaultValues: { otp: '' },
    mode: 'onChange',
  })

  const handleSubmit = async ({ otp }: { otp: string }) => {
    try {
      const result = await onSubmit(otp)

      switch (result.kind) {
        case 'otp-error': {
          form.setError('otp', { message: OTP_ERROR_MESSAGES[result.code] })
          form.setValue('otp', '')
          break
        }
        case 'api-error': {
          form.setValue('otp', '')
          toast.fromError(result.error)
          break
        }
      }
    } catch {
      toast.fromPreset('NETWORK_ERROR')
    }
  }

  return (
    <div>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Controller
          control={form.control}
          name="otp"
          render={({ field }) => (
            <InputOTP
              autoFocus
              maxLength={6}
              containerClassName="w-full"
              {...field}
              onComplete={form.handleSubmit(handleSubmit)}
            >
              <InputOTPGroup className="w-full">
                <InputOTPSlot index={0} className="flex-1" />
                <InputOTPSlot index={1} className="flex-1" />
                <InputOTPSlot index={2} className="flex-1" />
                <InputOTPSlot index={3} className="flex-1" />
                <InputOTPSlot index={4} className="flex-1" />
                <InputOTPSlot index={5} className="flex-1" />
              </InputOTPGroup>
            </InputOTP>
          )}
        />
      </form>

      {/* Error message */}
      <p
        className={text({
          font: 'beausite',
          size: 12,
          color: 'orange',
          weight: 'bold',
          // has minimum height of 2 line heights, to prevent layout shift when error message appears
          className: 'mt-2 min-h-[2lh] text',
        })}
      >
        {form.formState.errors.otp?.message}
      </p>

      {/* Area for loading state and code resend button, also has minimum height to prevent card size from changing too much */}
      <div className="mt-4 min-h-25">
        {form.formState.isSubmitting ? <ShimmerText>Checking Code Validity</ShimmerText> : children}
      </div>
    </div>
  )
}

function ResendCode({ onClick }: { onClick: () => Promise<void> }) {
  const [isResending, setIsResending] = useState(false)
  const { isAvailable, nextResendTimestamp } = otpVerification.useResendOtpAvailability()

  const handleResend = async () => {
    setIsResending(true)
    try {
      await onClick()
    } finally {
      setIsResending(false)
    }
  }

  if (isResending) {
    return <ShimmerText>Sending Code</ShimmerText>
  }

  if (isAvailable) {
    return (
      <Button variant="link" flex className="w-full" onClick={handleResend}>
        Resend Code
      </Button>
    )
  }

  return (
    <div className="flex items-center gap-2 justify-between">
      {/* Disabled resend code text */}
      <Text size={16} font="nbArchitekt" className="text-[#6a6a6a] cursor-not-allowed">
        Resend Code
      </Text>
      <Countdown
        key={nextResendTimestamp} // to reset the component when the timestamp changes
        endTimestamp={nextResendTimestamp}
        data-testid={TEST_IDS.resendCodeCountdown}
      />
    </div>
  )
}
